/**
 * Safe Zones — user-facing string catalog.
 *
 * Single source of truth for every string the safe-zones surface paints.
 * Components import from here rather than inlining strings so:
 * - copy edits land in one place
 * - translation (Phase 2 i18n / per-locale catalogs) is a swap, not a hunt
 * - QA can diff this file to see every public-facing change in a PR
 *
 * **Conventions**
 * - Group strings by surface (dropdown / hint / overlay), not by component.
 * - Use `as const` so consumers get literal types.
 * - Where a string needs a runtime value, expose a small function — keeps
 *   interpolation explicit and grep-able. Avoid string concatenation at
 *   call sites.
 * - Channel and format labels live here because they're user-visible; the
 *   enum-key shapes stay in `types.ts`. Display labels mirror those keys.
 *
 * **When adding a new overlay**
 * Add an entry under `overlays.<channel><Format>` containing every string
 * that overlay paints. Follow the existing structure: `captionPlaceholder`,
 * `captionsHint`, `controlsHint`, etc.
 */
export const SAFE_ZONE_STRINGS = {
  dropdown: {
    /** Persistent left-of-trigger label. */
    label: 'Preview',
    /** Trigger value when no overlay is selected. */
    none: 'None',
    /** Tooltip when the dropdown trigger is disabled. */
    disabledLoading: 'Loading preview...',
  },

  channels: {
    linkedin: 'LinkedIn',
    x: 'X',
    tiktok: 'TikTok',
    instagram: 'Instagram',
    facebook: 'Facebook',
    threads: 'Threads',
    youtube: 'YouTube',
  },

  formats: {
    feed_post: 'Feed Post',
    short_form_video: 'Short-Form Video',
    long_form_video: 'Long-Form Video',
    photo_carousel: 'Photo Carousel',
    story: 'Story',
    article: 'Article',
  },

  variants: {
    showPlayControls: { on: 'Player Controls On', off: 'Player Controls Off' },
    showCaptions: { on: 'Captions On', off: 'Captions Off' },
  },

  hint: {
    /** Header above the description block in a hint tooltip. */
    descriptionHeader: 'Description',
    /** Header above the pro-tip block — singular form (one tip). */
    proTipSingular: 'Pro Tip',
    /** Header above the pro-tip block — plural form (multiple tips). */
    proTipPlural: 'Pro Tips',
  },

  brand: {
    /** Used by `<BrandName />` when the showcase has no title. */
    fallbackName: 'Your company',
    /** Used by `<BrandHandle />` when the showcase has no slug. */
    fallbackHandle: '@yourcompany',
  },

  overlays: {
    linkedinFeedPost: {
      captionPlaceholder:
        'LinkedIn captions when turned on. Click CC to turn off.',
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on using the CC button.',
        ],
        proTips: [
          'Make sure that your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      controlsHint: {
        title: 'Player Controls',
        description: [
          'These controls appear when you hover over the video with your mouse in desktop view.',
          'They disappear after a few seconds.',
        ],
        proTips: [
          'Avoid anything important here because, as people scroll, if the mouse is over the video during the scroll, it will cover important information.',
        ],
      },
      aria: {
        play: 'Play',
        pause: 'Pause',
        seek: 'Seek',
        backToStart: 'Back to start',
        toggleCaptions: 'Toggle captions',
        mute: 'Volume',
        volumeSlider: 'Volume level',
        fullscreen: 'Fullscreen',
      },
    },
    youtubeLongFormVideo: {
      captionPlaceholder:
        'YouTube captions when turned on. Click CC to turn off.',
      /**
       * Fallback for the top title strip when the active story has no
       * title set. Phase 1 paints meta-copy describing what the slot
       * holds in production — overlays render the live story title
       * when one exists.
       */
      titleFallback: 'Your video title',
      /** Top-right link-bug pill text — mirrors YouTube's embed-player chrome. */
      watchOnYoutubeLabel: 'YouTube',
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on using the CC button.',
        ],
        proTips: [
          'Make sure your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      controlsHint: {
        title: 'Player Controls',
        description: [
          'These controls and the title strip auto-hide during playback and reappear when a viewer hovers over the player.',
          'In this preview, Play / Pause and the CC button drive the real video.',
          'The volume slider is visual-only — it does not change playback volume.',
        ],
        proTips: [
          'Avoid placing important content along the bottom or top edge — viewer mouse movement can summon the chrome and cover anything sitting there.',
        ],
      },
      aria: {
        play: 'Play',
        pause: 'Pause',
        seek: 'Seek',
        mute: 'Mute',
        unmute: 'Unmute',
        volumeSlider: 'Volume level',
        toggleCaptions: 'Toggle captions',
        settings: 'Settings',
        miniplayer: 'Miniplayer',
        theater: 'Theater mode',
        fullscreen: 'Fullscreen',
        autoplayToggle: 'Autoplay',
      },
    },
    youtubeShortFormVideo: {
      /**
       * Caption placeholder copy. Hint differs from the long-form watch
       * page — Shorts has no in-overlay CC button (no controls bar), so
       * the toggle lives in the toolbar pill.
       */
      captionPlaceholder:
        'YouTube captions when turned on. Toggle the Captions pill to turn off.',
      /** Top-left wordmark text beside the play-button glyph. */
      shortsLabel: 'Shorts',
      /** Subscribe pill text on the bottom brand row. */
      subscribeLabel: 'Subscribe',
      /**
       * Fallback for the title row when the active story has no title set.
       * Phase 1 paints meta-copy describing what the slot holds in
       * production — overlays render the live story title when one exists.
       */
      titleFallback: 'Your video title',
      /**
       * Fallback for the sound-attribution row's audio-source name. Resolves
       * to `"<soundFallback> · <BrandName />"` so the row always paints
       * *something* attribution-shaped.
       */
      soundFallback: 'Original audio',
      moreLink: '...more',
      /** Placeholder shown inside the bottom comment input affordance. */
      commentPlaceholder: 'Add a comment...',
      /**
       * Static placeholder labels under each action-rail icon. YouTube
       * Shorts paints a numeric count under Like and Comment, but the
       * Dislike rail item shows the literal word "Dislike" instead of a
       * number (the actual count is hidden).
       */
      counts: {
        like: '1.8K',
        dislike: 'Dislike',
        comment: '68',
      },
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on.',
          'Toggle the Captions pill in the toolbar to enable or disable them.',
        ],
        proTips: [
          'Make sure that your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      aria: {
        shorts: 'Shorts',
        back: 'Back',
        search: 'Search',
        more: 'More options',
        like: 'Like',
        dislike: 'Dislike',
        comment: 'Comments',
        share: 'Share',
        remix: 'Remix',
        sound: 'View sound',
        subscribe: 'Subscribe',
        seek: 'Seek',
        commentInput: 'Add a comment',
      },
    },
    facebookFeedPost: {
      captionPlaceholder:
        'Facebook captions when turned on. Toggle the Captions pill to turn off.',
      /**
       * Sub-line under the display name in the top author header. Phase 1
       * paints meta-copy describing the slot — wire to a real source
       * (post-age, sponsored flag, audience visibility) in a later phase.
       */
      subtitleFallback: 'Sponsored · 2h',
      /**
       * Used by the bottom caption-preview row when the active story has no
       * description set. Phase 1 paints meta-copy describing what the slot
       * will hold in production.
       */
      captionPreviewFallback: 'Description of the story.',
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on.',
          'Toggle the Captions pill in the toolbar to enable or disable them.',
        ],
        proTips: [
          'Make sure that your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      controlsHint: {
        title: 'Player Controls',
        description: [
          'Facebook paints a player chrome bar (play / time / progress / settings / captions / picture-in-picture / fullscreen / mute) along the bottom of the video.',
          'On 1:1 (square) posts the author header also paints on top of the video; 16:9 leaves the top edge clear because the header sits above the video in the surrounding feed UI.',
          'In this preview, Play / Pause and the CC button drive the real video. The volume and settings affordances are visual-only.',
        ],
        proTips: [
          'Avoid placing important content along the bottom edge — Facebook player chrome will sit there once the post is published.',
        ],
      },
      aria: {
        more: 'More options',
        toggleCaptions: 'Toggle captions',
        mute: 'Mute',
        unmute: 'Unmute',
        verified: 'Verified',
        play: 'Play',
        pause: 'Pause',
        seek: 'Seek',
        settings: 'Settings',
        miniplayer: 'Miniplayer',
        fullscreen: 'Fullscreen',
      },
    },
    instagramFeedPost: {
      /**
       * Static placeholder text shown in the time-ago slot beside the
       * account name (e.g. `· 2h`). Phase 1 paints meta-copy — wire to a
       * real `createdAt`-derived value in a later phase.
       */
      timeAgoFallback: '2h',
      /**
       * Audio-source attribution shown on the second row of the top profile
       * block (e.g. `@yourcompany · Original audio`). Resolves to
       * `<BrandHandle /> · <audioFallback>` so the row always paints
       * *something* audio-shaped — same posture as TikTok / YouTube Shorts
       * sound rows.
       */
      audioFallback: 'Original audio',
      followLabel: 'Follow',
      controlsHint: {
        title: 'Player Controls',
        description: [
          'Instagram paints the post header (avatar, handle, audio source, Follow button, ellipsis) along the top of the video, plus a tagged-accounts glyph in the bottom-left and a mute toggle in the bottom-right.',
          'These cover the edges of your frame.',
        ],
        proTips: [
          'Avoid placing important content along the top edge — Instagram chrome will sit there once the post is published.',
          'Bottom corners host two small affordances (tagged accounts on the left, mute toggle on the right). Important content should sit clear of both.',
        ],
      },
      aria: {
        more: 'More options',
        follow: 'Follow',
        verified: 'Verified',
        mute: 'Mute',
        unmute: 'Unmute',
        taggedAccounts: 'Tagged accounts',
      },
    },
    instagramShortFormVideo: {
      /**
       * Fallback for the description-preview row when the active story has
       * no description set. Phase 1 paints meta-copy describing what the
       * slot will hold in production — overlays render the live story
       * description when one exists.
       */
      descriptionFallback: 'Description of the story.',
      moreLink: '...more',
      followLabel: 'Follow',
      /**
       * Top-bar tab labels — Reels paints a two-tab centered title row.
       * `reels` is the active tab; `friends` is the inactive tab (paired
       * with a small face-cluster glyph).
       */
      tabs: {
        reels: 'Reels',
        friends: 'Friends',
      },
      /**
       * Static placeholder counts that mirror Reels' typical engagement
       * grammar. Repost, send, more, and the audio-source tile show no
       * count under their glyphs on the real surface.
       */
      counts: {
        like: '1.4M',
        comment: '20.9K',
        repost: '297K',
      },
      aria: {
        create: 'Create',
        reelsTab: 'Reels',
        friendsTab: 'Friends',
        preferences: 'Preferences',
        like: 'Like',
        comment: 'Comment',
        repost: 'Share',
        send: 'Send',
        more: 'More options',
        audioSource: 'View audio source',
        follow: 'Follow',
        verified: 'Verified',
        seek: 'Seek',
      },
    },
    linkedinShortFormVideo: {
      /**
       * Caption placeholder copy. Hint differs from Feed Post — the shorts
       * overlay has no in-overlay CC button (no controls bar), so the toggle
       * lives in the toolbar pill.
       */
      captionPlaceholder:
        'LinkedIn captions when turned on. Toggle the Captions pill to turn off.',
      /**
       * Fallback for the description-preview row when the active story
       * has no description set. Phase 1 paints meta-copy describing what
       * the slot will hold — overlays render the live story description
       * when one exists.
       */
      descriptionFallback: 'Description of the story.',
      moreLink: '...more',
      followLabel: 'Follow',
      /**
       * Sub-line under the account name. Phase 1 paints a meta-copy
       * placeholder describing what the slot will hold in production —
       * either the individual's title (e.g. "CEO of …") or the
       * organization's tagline / handle. Wire to a real source in a later
       * phase.
       */
      subtitle: 'Title of the individual or description of org',
      /** Static placeholder counts that mirror LinkedIn's shorts UI grammar. */
      counts: {
        like: '1.8K',
        comment: '68',
      },
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on.',
          'Toggle the Captions pill in the toolbar to enable or disable them.',
        ],
        proTips: [
          'Make sure that your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      aria: {
        back: 'Back',
        like: 'Like',
        comment: 'Comment',
        share: 'Share',
        bookmark: 'Save',
        more: 'More options',
        follow: 'Follow',
        seek: 'Seek',
      },
    },
    tiktokShortFormVideo: {
      /**
       * Fallback for the description-preview row when the active story has no
       * description set. Includes two static hashtag suffixes so the row reads
       * as TikTok-shaped grammar.
       *
       * TikTok has no auto-captions on the viewer side (no platform-level CC
       * toggle), so this overlay declares no `showCaptions` variant — there's
       * no `captionPlaceholder` / `captionsHint` block.
       */
      descriptionFallback: 'Description of the story. #fyp #foryoupage',
      /**
       * Fallback for the sound-attribution row's audio-source name. Resolves
       * to `"<soundFallback> · <BrandName />"` so the row always paints
       * *something* attribution-shaped.
       */
      soundFallback: 'Original sound',
      moreLink: '...more',
      followLabel: 'Follow',
      /**
       * Top-bar tab labels — TikTok paints a three-tab centered title row.
       * `forYou` is the active tab. Tabs are static placeholders; toggling
       * between feeds is not in scope.
       */
      tabs: {
        live: 'Live',
        following: 'Following',
        forYou: 'For You',
      },
      /** Static placeholder counts that mirror TikTok's typical engagement grammar. */
      counts: {
        like: '124.5K',
        comment: '892',
        bookmark: '3.4K',
        share: '1.2K',
      },
      aria: {
        liveTab: 'Live',
        followingTab: 'Following',
        forYouTab: 'For You',
        search: 'Search',
        profile: 'Profile',
        follow: 'Follow',
        like: 'Like',
        comment: 'Comment',
        bookmark: 'Save',
        share: 'Share',
        sound: 'View sound',
        seek: 'Seek',
      },
    },
    threadsFeedPost: {
      /**
       * Threads paints almost nothing inside the video rect — the only piece
       * of chrome the safe-zone preview shows is a small mute speaker in the
       * bottom-right corner. Threads has no automatic / burned-in captions,
       * and its player controls (scrubber, play, time, fullscreen) live
       * outside the rect in the surrounding feed UI. So the strings block
       * only needs the mute affordance's aria labels.
       */
      aria: {
        mute: 'Mute',
        unmute: 'Unmute',
      },
    },
    xFeedPost: {
      /**
       * Caption placeholder copy. X paints captions as plain white text with
       * a soft black drop-shadow — no background pill — so the placeholder
       * reads the same in this preview.
       */
      captionPlaceholder:
        'X captions when turned on. Click CC to turn off.',
      captionsHint: {
        title: 'Captions',
        description: [
          'These captions only appear if they are turned on using the CC button in this interactive preview.',
        ],
        proTips: [
          'Make sure that your captions are not conflicting with these captions so that both can be on screen and still look nice.',
        ],
      },
      controlsHint: {
        title: 'Player Controls',
        description: [
          'These controls and the scrubber appear at the bottom of the player while a viewer is interacting with the video.',
          'In this preview, Play / Pause, the scrubber, and the CC button drive the real video.',
          'Mute, settings, picture-in-picture, and fullscreen are visual-only — they do not change playback.',
        ],
        proTips: [
          'Avoid placing important content along the bottom edge — the controls bar covers it whenever a viewer interacts with the card.',
        ],
      },
      /**
       * Hover-tooltip copy for the CC button in the controls bar — mirrors
       * what X's real player surfaces ("Hide captions" while captions are
       * on, "Show captions" while off).
       */
      ccTooltip: {
        show: 'Show captions',
        hide: 'Hide captions',
      },
      aria: {
        play: 'Play',
        pause: 'Pause',
        seek: 'Seek',
        mute: 'Mute',
        unmute: 'Unmute',
        settings: 'Settings',
        pictureInPicture: 'Picture-in-picture',
        fullscreen: 'Fullscreen',
        toggleCaptions: 'Toggle captions',
      },
    },
  },
} as const;
